import { useState, useRef, useEffect, useMemo, useCallback, Fragment, type JSX } from 'react'
import {
  useFacebookChatMessages,
  type FacebookChatMessage,
  type PendingFacebookMessage,
  type FacebookAttachment,
} from '../../hooks/useFacebookChats.js'
import { FacebookIcon } from '../conversations/BrandIcons.js'
import { TicketGroupBlock } from '../chat-shared/TicketGroupBlock.js'
import { groupMessagesByTicket } from '../chat-shared/ticketGrouping.js'
import { dayBoundaries } from '../chat-shared/dayGrouping.js'
import { placeHandoffEvents } from '../chat-shared/timeline.js'
import { HandoffDivider } from '../chat-shared/HandoffDivider.js'
import { useStickyScroll } from '../chat-shared/useStickyScroll.js'
import { useChannelTyping } from '../chat-shared/useChannelTyping.js'
import { useTypingSignal } from '../chat-shared/useTypingSignal.js'
import { TypingIndicator } from '../chat-shared/TypingIndicator.js'
import { Send, MessageCircle, Ticket, X, Loader2, Check, AlertCircle, ChevronDown, ArrowRightLeft, FileText, Film, Bot, UserPlus } from 'lucide-react'
import { formatInTimezone } from '../../utils/timezone.js'
import { ToolButton } from '../../components/conversations/detail/primitives.js'
import { FacebookPostBanner } from '../../components/facebook/FacebookPostBanner.js'
import { formatCustomerName, cleanIdentifier } from '../../utils/formatCustomerName.js'
import { api } from '../../api/client.js'

interface Props {
  chatId: string | null
  isAssignedToMe?: boolean
  isAiAssigned?: boolean
  aiAgentName?: string | null
  onOpenTransfer?: () => void
  onOpenDetails?: () => void
}

/** Render a single attachment (image, sticker, video, etc.) */
function AttachmentPreview({ attachment }: { attachment: FacebookAttachment }): JSX.Element {
  const url = attachment.payload?.url
  
  if (!url) {
    return (
      <div className="flex items-center gap-1 text-xs text-gray-400">
        <FileText className="h-4 w-4" />
        <span>Attachment</span>
      </div>
    )
  }

  if (attachment.type === 'image' || attachment.type === 'sticker') {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={url}
          alt={attachment.type === 'sticker' ? 'Sticker' : 'Image'}
          className="max-h-48 max-w-full rounded-lg object-contain"
          onError={(e) => {
            // Hide broken images
            (e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      </a>
    )
  }

  if (attachment.type === 'video') {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs underline">
        <Film className="h-4 w-4" />
        <span>Video</span>
      </a>
    )
  }

  if (attachment.type === 'audio') {
    return (
      <audio controls className="max-w-full">
        <source src={url} />
        Your browser does not support audio.
      </audio>
    )
  }

  // Fallback for other types
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs underline">
      <FileText className="h-4 w-4" />
      <span>{attachment.type || 'File'}</span>
    </a>
  )
}

function MessageBubble({ msg, showReplyLabel }: { msg: FacebookChatMessage; showReplyLabel?: boolean }): JSX.Element {
  const isOutbound = msg.direction === 'outbound'
  // Outbound rows stored by the worker start as 'sending' and end 'sent' or 'failed'.
  const failed = isOutbound && msg.status === 'failed'
  const sending = isOutbound && msg.status === 'sending'
  const attachments = msg.raw_payload?.message?.attachments ?? []
  const hasAttachments = attachments.length > 0
  const hasText = !!msg.content_text?.trim()
  const isReply = showReplyLabel && !isOutbound

  return (
    <div className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'}`}>
      {/* Who replied — outbound only; the inbound side is named in the header. */}
      {isOutbound && msg.agent_name && (
        <span className="mb-0.5 pr-1 text-[10px] font-medium text-gray-500">{msg.agent_name}</span>
      )}
      {/* Reply label for inbound comments */}
      {isReply && (
        <span className="mb-0.5 pl-1 text-[10px] font-medium text-blue-600">Reply</span>
      )}
      <div
        className={`relative max-w-[70%] rounded-2xl px-4 py-2 text-sm ${
          failed ? 'border border-red-200 bg-red-50 text-gray-700' : isOutbound ? 'bg-[#1877F2] text-white' : 'border border-gray-100 bg-white text-gray-800 shadow-sm'
        }`}
      >
        {/* Render attachments */}
        {hasAttachments && (
          <div className="mb-2 flex flex-col gap-2">
            {attachments.map((att, idx) => (
              <AttachmentPreview key={idx} attachment={att} />
            ))}
          </div>
        )}
        {/* Render text content */}
        {hasText && (
          <p className="whitespace-pre-wrap break-words">{msg.content_text}</p>
        )}
        {/* Show placeholder only if no text and no attachments rendered */}
        {!hasText && !hasAttachments && (
          <p className="whitespace-pre-wrap break-words text-gray-400">[media]</p>
        )}
        <div className="mt-1 flex items-center justify-end gap-1">
          <span className={`text-[10px] ${isOutbound ? 'text-blue-100' : 'text-gray-400'}`}>
            {formatInTimezone(msg.sent_at, { hour: '2-digit', minute: '2-digit' })}
          </span>
          {failed && <span className="text-[10px] text-red-500">Not sent</span>}
          {sending && <span className="text-[10px] text-blue-100">Sending…</span>}
          {isOutbound && !failed && !sending && <Check className="h-3 w-3 text-blue-100" />}
        </div>
      </div>
    </div>
  )
}

/** Centered date chip marking where a new day starts in the thread. */
function DaySeparator({ label }: { label: string }): JSX.Element {
  return (
    <div className="my-2 flex justify-center">
      <span className="rounded-full bg-white px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-gray-500 shadow-sm">
        {label}
      </span>
    </div>
  )
}

/** The not-yet-confirmed twin of the bubble above — dimmed, with a spinner. */
function PendingBubble({
  msg,
  onDismiss,
}: {
  msg: PendingFacebookMessage
  onDismiss: (localId: string) => void
}): JSX.Element {
  const failed = msg.status === 'failed'
  return (
    <div className="flex justify-end">
      <div
        className={`relative max-w-[70%] rounded-2xl px-4 py-2 text-sm ${
          failed ? 'border border-red-200 bg-red-50 text-gray-700' : 'bg-[#1877F2] text-white opacity-70'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{msg.content_text}</p>
        <div className="mt-1 flex items-center justify-end gap-1">
          <span className={`text-[10px] ${failed ? 'text-red-500' : 'text-blue-100'}`}>
            {failed ? 'Not sent' : 'Sending…'}
          </span>
          {failed ? (
            <AlertCircle className="h-3 w-3 text-red-500" />
          ) : (
            <Loader2 className="h-3 w-3 animate-spin text-blue-100" />
          )}
          {failed && (
            <button
              onClick={() => onDismiss(msg.localId)}
              className="ml-0.5 text-gray-400 hover:text-gray-600"
              title="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function FacebookChatThread({ chatId, isAssignedToMe, isAiAssigned, aiAgentName, onOpenTransfer, onOpenDetails }: Props): JSX.Element {
  const {
    chat,
    messages,
    handoffEvents,
    replyWindowGuard,
    conversations,
    pending,
    loading,
    loadingMore,
    hasMore,
    sendMessage,
    createTicket,
    dismissPending,
    loadMore,
  } = useFacebookChatMessages(chatId)
  const [input, setInput] = useState('')
  const { actors: typingActors } = useChannelTyping('facebook', chatId)
  const { signalTyping, stopTyping } = useTypingSignal('facebook', chatId)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creatingTicket, setCreatingTicket] = useState(false)
  const [ticketResult, setTicketResult] = useState<{ referenceId: string } | null>(null)
  // Only follows new messages while the agent is already at the bottom.
  const fetchingMoreRef = useRef(false)
  const { containerRef, atBottom, unseen, scrollToBottom, handleScroll: stickyHandleScroll } = useStickyScroll(
    `${chatId}:${messages.length}:${pending.length}`,
  )

  const onScroll = useCallback(() => {
    stickyHandleScroll()
    if (fetchingMoreRef.current || !hasMore || loadingMore) return
    const el = containerRef.current
    if (!el) return
    if (el.scrollTop <= el.clientHeight * 0.2) {
      const oldScrollHeight = el.scrollHeight
      const oldScrollTop = el.scrollTop
      fetchingMoreRef.current = true
      void loadMore().finally(() => {
        requestAnimationFrame(() => {
          const next = containerRef.current
          if (!next) return
          const added = next.scrollHeight - oldScrollHeight
          if (added > 0) {
            next.scrollTop = oldScrollTop + added
          }
          fetchingMoreRef.current = false
        })
      })
    }
  }, [hasMore, loadingMore, loadMore, stickyHandleScroll, containerRef])

  // Only enforced when the tenant has channel_chat_guards ON (server-reported). Fails open:
  // with no inbound message loaded we can't tell, so the server remains the final check.
  const windowExpired = useMemo(() => {
    if (!replyWindowGuard) return false
    const lastInbound = messages
      .filter((m) => m.direction === 'inbound')
      .reduce((max, m) => Math.max(max, new Date(m.sent_at).getTime()), 0)
    if (lastInbound === 0) return false
    return Date.now() - lastInbound > 24 * 60 * 60 * 1000
  }, [messages, replyWindowGuard])
  const groups = useMemo(() => groupMessagesByTicket(messages), [messages])
  const handoffPlacement = useMemo(
    () => placeHandoffEvents(messages, handoffEvents),
    [messages, handoffEvents],
  )
  const conversationById = useMemo(
    () => new Map(conversations.map((c) => [c.id, c])),
    [conversations],
  )
  const unlinkedCount = useMemo(
    () => messages.filter((m) => !m.conversation_id).length,
    [messages],
  )
  // Boundaries are computed over the flattened render order, so a day chip
  // appears once per calendar day rather than once per conversation group.
  const dayStarts = useMemo(
    () => dayBoundaries(groups.flatMap((g) => g.messages)),
    [groups],
  )

  // Track which messages should show the Facebook post banner
  // (first message for each unique post_id)
  const postBannerShown = useMemo(() => {
    const seenPosts = new Set<string>()
    const bannerMap = new Map<string, boolean>()
    for (const msg of messages) {
      const postId = msg.raw_payload?.fb_post_id as string | undefined
      if (postId && !seenPosts.has(postId)) {
        seenPosts.add(postId)
        bannerMap.set(msg.id, true)
      }
    }
    return bannerMap
  }, [messages])

  // Track which messages are replies (not the first comment on a post)
  const replyMap = useMemo(() => {
    const replyFlags = new Map<string, boolean>()
    const postFirstMessages = new Map<string, string>() // post_id -> first message id
    for (const msg of messages) {
      const postId = msg.raw_payload?.fb_post_id as string | undefined
      if (postId && msg.direction === 'inbound') {
        if (!postFirstMessages.has(postId)) {
          postFirstMessages.set(postId, msg.id)
        } else {
          replyFlags.set(msg.id, true)
        }
      }
    }
    return replyFlags
  }, [messages])

  useEffect(() => {
    setTicketResult(null)
    setError(null)
    setInput('')
  }, [chatId])

  async function handleCreateTicket(): Promise<void> {
    if (!isAssignedToMe || creatingTicket || unlinkedCount === 0) return
    setCreatingTicket(true)
    setError(null)
    try {
      // No message ids — the API promotes everything not already on a conversation.
      const result = await createTicket()
      if (result) setTicketResult({ referenceId: result.referenceId })
    } catch {
      setError('Could not create the conversation.')
    } finally {
      setCreatingTicket(false)
    }
  }

  async function handleSend(): Promise<void> {
    if (!isAssignedToMe || !chat || (chat.status !== 'active' && chat.status !== 'open') || windowExpired) return
    if (!input.trim() || sending) return
    stopTyping()
    setSending(true)
    setError(null)
    try {
      await sendMessage(input)
      setInput('')
    } catch (err) {
      const code = (err as { body?: { error?: string } })?.body?.error
      setError(
        code === 'outside_messaging_window'
          ? 'Customer must message first (24h window expired).'
          : 'Message could not be sent.',
      )
    } finally {
      setSending(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  if (!chatId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50">
        <MessageCircle className="mb-3 h-12 w-12 text-gray-300" />
        <p className="text-sm text-gray-400">Select a Facebook chat to start messaging</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2]/10">
          <FacebookIcon className="h-5 w-5 text-[#1877F2]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-800">
            <button type="button" onClick={onOpenDetails} className="hover:text-blue-600">
              {formatCustomerName(chat?.sender_name, chat?.sender_id, 'facebook')}
            </button>
          </h3>
          <p className="truncate text-xs text-gray-400">
            {chat?.sender_email ? chat.sender_email : 'Facebook Messenger'}
          </p>
        </div>
        <button
          onClick={() => void handleCreateTicket()}
          disabled={!isAssignedToMe || !chat || (chat.status !== 'active' && chat.status !== 'open') || unlinkedCount === 0 || creatingTicket}
          title={
            unlinkedCount === 0
              ? 'Every message is already on a conversation'
              : `Create a conversation from ${unlinkedCount} unlinked message${unlinkedCount === 1 ? '' : 's'}`
          }
          className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
        >
          <Ticket className="h-3.5 w-3.5" />
          {creatingTicket ? 'Creating…' : 'Create Conversation'}
          {unlinkedCount > 0 && !creatingTicket && (
            <span className="rounded-full bg-[#1877F2]/15 px-1.5 text-[10px] font-semibold text-[#1877F2]">
              {unlinkedCount}
            </span>
          )}
        </button>
        {(isAssignedToMe || isAiAssigned) && onOpenTransfer && (
          <ToolButton label="Transfer" icon={<ArrowRightLeft className="h-4 w-4" />} onClick={onOpenTransfer} />
        )}
      </div>

      {isAiAssigned && (
        <div className="flex items-center justify-between border-b border-purple-200 bg-purple-50 px-4 py-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-purple-700">
            <Bot className="h-3.5 w-3.5" />
            {aiAgentName ?? 'AI Agent'} is handling this chat
          </span>
          {!isAssignedToMe && (
            <button
              type="button"
              onClick={() => void api.post(`/agent/facebook-chats/${chatId}/assign`, {}).then(() => {}).catch(() => {})}
              className="flex items-center gap-1.5 rounded-lg border border-purple-300 bg-white px-3 py-1 text-xs font-medium text-purple-700 transition-all hover:bg-purple-50 active:scale-[0.97]"
            >
              <UserPlus className="h-3 w-3" />
              Assign to Me
            </button>
          )}
        </div>
      )}

      {ticketResult && (
        <div className="flex items-center justify-between border-b border-green-200 bg-green-50 px-4 py-2">
          <span className="text-xs text-green-700">
            Conversation created: <strong>{ticketResult.referenceId}</strong>
          </span>
          <button onClick={() => setTicketResult(null)} className="text-gray-400 hover:text-gray-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Messages, grouped by the conversation they belong to */}
      <div className="relative flex-1 overflow-hidden">
      <div ref={containerRef} onScroll={onScroll} className="h-full overflow-y-auto thin-scrollbar px-4 py-4">
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
            Loading older messages…
          </div>
        )}
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-gray-400">Loading messages…</div>
        ) : messages.length === 0 && pending.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-gray-400">No messages yet</div>
        ) : (
          <div className="flex flex-col gap-2">
            {groups.map((group) =>
              group.type === 'ticket' ? (
                <TicketGroupBlock
                  key={group.key}
                  conversationId={group.conversationId}
                  conversation={conversationById.get(group.conversationId)}
                  messageCount={group.messages.length}
                  accentClassName="text-[#1877F2]"
                >
                  {group.messages.map((msg) => (
                    <Fragment key={msg.id}>
                      {dayStarts.has(msg.id) && <DaySeparator label={dayStarts.get(msg.id)!} />}
                      {handoffPlacement.before.get(msg.id)?.map((e) => <HandoffDivider key={e.id} event={e} />)}
                      {postBannerShown.get(msg.id) && (
                        <FacebookPostBanner
                          compact
                          postId={(msg.raw_payload?.fb_post_id as string | undefined) ?? null}
                          postMessage={(msg.raw_payload?.post_message as string | undefined) ?? null}
                          postPicture={(msg.raw_payload?.post_picture as string | undefined) ?? null}
                          permalink={(msg.raw_payload?.permalink as string | undefined) ?? null}
                          accountHolderName={
                            (msg.raw_payload?.author_name as string | undefined) ||
                            chat?.sender_name ||
                            'Facebook User'
                          }
                          accountId={
                            (msg.raw_payload?.fb_post_id as string | undefined) ||
                            (msg.raw_payload?.author_id as string | undefined) ||
                            cleanIdentifier(chat?.sender_id) ||
                            null
                          }
                        />
                      )}
                      <MessageBubble msg={msg} showReplyLabel={replyMap.get(msg.id) ?? false} />
                    </Fragment>
                  ))}
                </TicketGroupBlock>
              ) : (
                group.messages.map((msg) => (
                  <Fragment key={msg.id}>
                    {dayStarts.has(msg.id) && <DaySeparator label={dayStarts.get(msg.id)!} />}
                    {handoffPlacement.before.get(msg.id)?.map((e) => <HandoffDivider key={e.id} event={e} />)}
                    {postBannerShown.get(msg.id) && (
                      <FacebookPostBanner
                        compact
                        postId={(msg.raw_payload?.fb_post_id as string | undefined) ?? null}
                        postMessage={(msg.raw_payload?.post_message as string | undefined) ?? null}
                        postPicture={(msg.raw_payload?.post_picture as string | undefined) ?? null}
                        permalink={(msg.raw_payload?.permalink as string | undefined) ?? null}
                        accountHolderName={
                          (msg.raw_payload?.author_name as string | undefined) ||
                          chat?.sender_name ||
                          'Facebook User'
                        }
                        accountId={
                          (msg.raw_payload?.fb_post_id as string | undefined) ||
                          (msg.raw_payload?.author_id as string | undefined) ||
                          cleanIdentifier(chat?.sender_id) ||
                          null
                        }
                      />
                    )}
                    <MessageBubble msg={msg} showReplyLabel={replyMap.get(msg.id) ?? false} />
                  </Fragment>
                ))
              ),
            )}
            {handoffPlacement.trailing.map((e) => <HandoffDivider key={e.id} event={e} />)}
            {pending.map((p) => (
              <PendingBubble key={p.localId} msg={p} onDismiss={dismissPending} />
            ))}
          </div>
        )}
      </div>

      {/* Jump back to the newest message; badge counts what arrived meanwhile. */}
      {!atBottom && (
        <button
          onClick={() => scrollToBottom()}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-lg ring-1 ring-gray-200 transition-colors hover:bg-gray-50"
          title="Jump to latest"
        >
          <ChevronDown className="h-4 w-4" />
          {unseen > 0 && (
            <span className="rounded-full bg-[#1877F2] px-1.5 py-0.5 text-[10px] font-bold text-white">
              {unseen}
            </span>
          )}
        </button>
      )}
      </div>

      <TypingIndicator actors={typingActors} />

      {error && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-1.5 text-[11px] text-red-600">{error}</div>
      )}

      {isAssignedToMe && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => { setInput(e.target.value); signalTyping() }}
            onKeyDown={handleKeyDown}
            placeholder={windowExpired ? 'Customer must message first (24h window expired)' : 'Type a reply…'}
            disabled={!isAssignedToMe || !chat || (chat.status !== 'active' && chat.status !== 'open') || windowExpired || sending}
            className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm outline-none focus:border-[#1877F2] focus:ring-1 focus:ring-[#1877F2]/20 disabled:opacity-50"
          />
          <button
            onClick={() => void handleSend()}
            disabled={!isAssignedToMe || !chat || (chat.status !== 'active' && chat.status !== 'open') || windowExpired || !input.trim() || sending}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2] text-white transition-colors hover:bg-[#0d6efd] disabled:opacity-40 disabled:hover:bg-[#1877F2]"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
      )}
    </div>
  )
}
